import { useEffect, useState } from 'react'
import { api, ApiError } from '../api'
import { Modal, Field, Alert, Spinner, Tabs, CopyButton } from './ui'

interface EmailKit {
  to: string[]
  cc: string[]
  bcc: string[]
  subject: string
  text: string
  html: string
  mailtoUrl: string
  emlBase64: string
  emlFilename: string
  hasAttachment: boolean
}

interface Provider {
  id: string
  kind: string
  name: string
  is_active: number | boolean
  priority: number
  from_email: string | null
}

/**
 * Compose window: edit recipients, subject and body, see the result rendered
 * live, then send. Nothing is transmitted until "Send now" is pressed.
 */
export default function SendEmail({
  invoiceId,
  invoiceNumber,
  onClose,
  onSent,
}: {
  invoiceId: string
  invoiceNumber: string
  onClose: () => void
  onSent: (message: string) => void
}) {
  const [kit, setKit] = useState<EmailKit | null>(null)
  const [providers, setProviders] = useState<Provider[]>([])
  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [bcc, setBcc] = useState('')
  const [subject, setSubject] = useState('')
  const [text, setText] = useState('')
  const [html, setHtml] = useState('')
  const [attachPdf, setAttachPdf] = useState(true)
  const [tab, setTab] = useState('preview')
  const [error, setError] = useState('')
  const [sending, setSending] = useState(false)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    ;(async () => {
      try {
        const [k, p] = await Promise.all([
          api.get<{ kit: EmailKit }>(`/invoices/${invoiceId}/email-kit`),
          api.get<{ providers: Provider[] }>('/settings/email/providers'),
        ])
        setKit(k.kit)
        setTo(k.kit.to.join(', '))
        setCc(k.kit.cc.join(', '))
        setBcc(k.kit.bcc.join(', '))
        setSubject(k.kit.subject)
        setText(k.kit.text)
        setHtml(k.kit.html)
        setAttachPdf(k.kit.hasAttachment)
        setProviders(p.providers)
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Could not prepare the email')
      }
    })()
  }, [invoiceId])

  const parseList = (s: string) =>
    s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean)

  const activeProvider = providers
    .filter((p) => p.is_active)
    .sort((a, b) => a.priority - b.priority)[0]

  const isManual = activeProvider?.kind === 'manual'

  const send = async () => {
    setError('')
    const toList = parseList(to)
    if (toList.length === 0) return setError('Add at least one recipient')
    if (!subject.trim()) return setError('Subject cannot be empty')

    setSending(true)
    try {
      const r = await api.post<{ ok: boolean; manual: boolean; error?: string; providerKind: string }>(
        `/invoices/${invoiceId}/send`,
        {
          to: toList,
          cc: parseList(cc),
          bcc: parseList(bcc),
          subject,
          text,
          html,
          attachPdf,
        },
      )
      if (r.manual) {
        onSent(
          'The active provider is Manual, so nothing was transmitted. Configure SMTP in Settings → Email providers, or use the Email kit to send it yourself.',
        )
      } else if (r.ok) {
        onSent(`Sent to ${toList.join(', ')} via ${r.providerKind}.`)
      } else {
        setError(r.error ?? 'Send failed')
        setSending(false)
        return
      }
      onClose()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Send failed')
      setSending(false)
    }
  }

  const markDirty = () => setDirty(true)

  return (
    <Modal
      wide
      title={`Send invoice ${invoiceNumber}`}
      onClose={onClose}
      footer={
        <>
          <span className="small muted" style={{ marginRight: 'auto' }}>
            {activeProvider
              ? `Sending via ${activeProvider.name}${activeProvider.from_email ? ` (${activeProvider.from_email})` : ''}`
              : 'No active provider'}
          </span>
          <button className="btn" onClick={onClose} disabled={sending}>
            Cancel
          </button>
          <button className="btn primary" onClick={send} disabled={sending || !kit}>
            {sending ? 'Sending…' : 'Send now'}
          </button>
        </>
      }
    >
      <Alert kind="error">{error}</Alert>

      {isManual && (
        <Alert kind="warn">
          The active provider is <strong>Manual</strong>, which never transmits anything. Set up SMTP
          in <strong>Settings → Email providers</strong> to actually send from here.
        </Alert>
      )}

      {!kit ? (
        <Spinner label="Preparing…" />
      ) : (
        <>
          <div className="grid c2">
            <Field label="To" hint="Comma separated.">
              <input type="text" value={to} onChange={(e) => { setTo(e.target.value); markDirty() }} />
            </Field>
            <Field label="CC">
              <input type="text" value={cc} onChange={(e) => { setCc(e.target.value); markDirty() }} />
            </Field>
          </div>
          <Field label="BCC">
            <input type="text" value={bcc} onChange={(e) => { setBcc(e.target.value); markDirty() }} />
          </Field>

          <Field label="Subject">
            <input type="text" value={subject} onChange={(e) => { setSubject(e.target.value); markDirty() }} />
          </Field>

          <label className="check" style={{ marginBottom: 12 }}>
            <input
              type="checkbox"
              checked={attachPdf}
              disabled={!kit.hasAttachment}
              onChange={(e) => setAttachPdf(e.target.checked)}
            />
            Attach the invoice PDF
            {!kit.hasAttachment && (
              <span className="muted small">
                {' '}
                — unavailable, PDF rendering is not configured. The email still carries the invoice link.
              </span>
            )}
          </label>

          <Tabs
            tabs={[
              { key: 'preview', label: 'Preview' },
              { key: 'text', label: 'Edit plain text' },
              { key: 'html', label: 'Edit HTML' },
            ]}
            active={tab}
            onChange={setTab}
          />

          {tab === 'preview' && (
            <div>
              <div
                style={{
                  border: '1px solid var(--line)',
                  borderRadius: 6,
                  overflow: 'hidden',
                  background: '#fff',
                }}
              >
                {/*
                  `allow-same-origin` without `allow-scripts` is deliberate.
                  A fully empty sandbox gives the frame an opaque origin, so the
                  inherited CSP `default-src 'self'` matches nothing and the
                  document renders blank. Granting same-origin fixes that, and
                  because scripts are still not allowed, nothing in the email
                  body can execute or reach the parent page.
                */}
                <iframe
                  title="Email preview"
                  srcDoc={html}
                  sandbox="allow-same-origin"
                  style={{ width: '100%', height: 380, border: 'none', display: 'block' }}
                />
              </div>
              <div className="hint" style={{ marginTop: 6 }}>
                This is exactly what the client receives.{' '}
                {dirty && <strong>Your edits are reflected above.</strong>}
              </div>
            </div>
          )}

          {tab === 'text' && (
            <Field hint="Sent as the plain-text alternative, and used by clients that block HTML.">
              <textarea
                rows={16}
                value={text}
                onChange={(e) => { setText(e.target.value); markDirty() }}
                style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5 }}
              />
            </Field>
          )}

          {tab === 'html' && (
            <Field hint="Edits here appear immediately in the Preview tab.">
              <textarea
                rows={16}
                value={html}
                onChange={(e) => { setHtml(e.target.value); markDirty() }}
                style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}
              />
            </Field>
          )}

          <div className="sep" />
          <div className="btn-row">
            <CopyButton text={subject} label="Copy subject" />
            <CopyButton text={text} label="Copy body" />
            <a className="btn sm" href={kit.mailtoUrl}>
              Open in mail app instead
            </a>
          </div>
        </>
      )}
    </Modal>
  )
}
